import { useState, useEffect } from 'react';
import { User, MapPin, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { teamService, teamSchedulingService } from '@/services/teamService';
import { providerJobService } from '@/services/providerJobService';
import { useAuthStore } from '@/store/authStore';
import type { TeamMember } from '@/types';

interface ScheduleEvent {
  id: string;
  memberId: string;
  memberName: string;
  jobId: string;
  jobTitle: string;
  jobLocation: string;
  startTime: Date;
  endTime: Date;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  customerName: string;
  estimatedDuration: number; // in hours
}

interface AvailableJob {
  id: string;
  title: string;
  customer: string;
  location: string;
  priority: 'low' | 'medium' | 'high';
  description?: string;
}

interface TeamSchedulerProps {
  teamMembers: TeamMember[];
  onScheduleUpdate?: () => void;
}

export function TeamScheduler({ teamMembers, onScheduleUpdate }: TeamSchedulerProps) {
  const { user } = useAuthStore();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'day'>('week');
  const [scheduleEvents, setScheduleEvents] = useState<ScheduleEvent[]>([]);
  const [selectedMember, setSelectedMember] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [availableJobs, setAvailableJobs] = useState<AvailableJob[]>([]);
  const [isJobsLoading, setIsJobsLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
    memberId: string;
    date: Date;
    hour: number;
  } | null>(null);

  useEffect(() => {
    loadScheduleData();
  }, [currentDate, selectedMember]);

  useEffect(() => {
    loadAvailableJobs();
  }, []);

  const loadScheduleData = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const events = await teamService.getTeamSchedule(user.id, currentDate.toISOString(), viewMode);
      // Transform TeamSchedule[] to ScheduleEvent[]
      const transformedEvents = events.map((event: any) => ({
        id: event.id,
        memberId: event.memberId || event.userId,
        memberName: event.memberName || event.userName,
        jobId: event.jobId,
        jobTitle: event.jobTitle || event.title,
        startTime: event.startTime,
        endTime: event.endTime,
        status: event.status,
        location: event.location,
        jobLocation: event.jobLocation || event.location,
        customerName: event.customerName,
        priority: event.priority || 'medium',
        type: event.type || 'job',
        estimatedDuration: event.estimatedDuration || 60
      }));
      setScheduleEvents(transformedEvents);
    } catch (error) {
      console.error('Failed to load schedule:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAvailableJobs = async () => {
    setIsJobsLoading(true);
    try {
      // Use getActiveJobs to get jobs with 'quote-accepted' status
      // These are jobs where customer accepted a quote and are ready to be assigned to team members
      const result = await providerJobService.getActiveJobs({ 
        status: 'quote-accepted',
        limit: 50 
      });
      const jobs = result.jobs || [];
      
      const mappedJobs: AvailableJob[] = jobs.map((job) => ({
        id: job.id,
        title: job.title,
        customer: job.customerName || 'Customer',
        location: job.location || '—',
        priority: job.urgency?.toLowerCase() === 'high'
          ? 'high'
          : job.urgency?.toLowerCase() === 'low'
            ? 'low'
            : 'medium',
        description: job.description
      }));
      setAvailableJobs(mappedJobs);
    } catch (error) {
      console.error('Failed to load available jobs:', error);
      setAvailableJobs([]);
    } finally {
      setIsJobsLoading(false);
    }
  };

  const getWeekDates = (date: Date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay() + 1); // Start from Monday
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getTimeSlots = () => {
    const slots = [];
    // Check if there are any events outside the default 8-20 range
    let minHour = 8;
    let maxHour = 20;
    
    if (scheduleEvents.length > 0) {
      scheduleEvents.forEach(event => {
        const startHour = new Date(event.startTime).getHours();
        const endHour = new Date(event.endTime).getHours();
        minHour = Math.min(minHour, startHour);
        maxHour = Math.max(maxHour, endHour + 1);
      });
      
      // Ensure reasonable bounds (0-24)
      minHour = Math.max(0, minHour);
      maxHour = Math.min(24, maxHour);
    }
    
    for (let hour = minHour; hour < maxHour; hour++) {
      slots.push(hour);
    }
    return slots;
  };

  const getEventsForMemberAndTime = (memberId: string, date: Date, hour: number) => {
    return scheduleEvents.filter(event => {
      const eventDate = new Date(event.startTime);
      const eventHour = eventDate.getHours();
      return (
        event.memberId === memberId &&
        eventDate.toDateString() === date.toDateString() &&
        eventHour === hour
      );
    });
  };

  const getMemberAvailability = (member: TeamMember, date: Date, hour: number) => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()] as keyof typeof member.availability;
    const availability = member.availability[dayOfWeek];
    
    if (!availability.available) return false;
    
    const startHour = parseInt(availability.startTime.split(':')[0]);
    const endHour = parseInt(availability.endTime.split(':')[0]);
    
    return hour >= startHour && hour < endHour;
  };

  const handleTimeSlotClick = (memberId: string, date: Date, hour: number) => {
    setSelectedTimeSlot({ memberId, date, hour });
    setShowAssignModal(true);
  };

  const handleEventClick = (event: ScheduleEvent) => {
    // Handle event click - could show event details, allow editing, etc.
    console.log('Event clicked:', event);
    // For now, just show an alert with event details
    alert(`Job: ${event.jobTitle}\nCustomer: ${event.customerName}\nLocation: ${event.jobLocation}\nStatus: ${event.status}\nPriority: ${event.priority}`);
  };

  const handleAssignJob = async (jobData: any) => {
    if (!selectedTimeSlot || !user?.id || !jobData.memberId) return;

    try {
      await teamSchedulingService.assignJobToMember(user.id, {
        memberId: jobData.memberId,
        jobId: jobData.jobId,
        scheduledDate: selectedTimeSlot.date,
        scheduledHour: selectedTimeSlot.hour,
        estimatedDuration: jobData.estimatedDuration
      });
      
      await loadScheduleData();
      setShowAssignModal(false);
      setSelectedTimeSlot(null);
      onScheduleUpdate?.();
    } catch (error) {
      console.error('Failed to assign job:', error);
      alert('Failed to assign job. Please try again.');
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    setCurrentDate(newDate);
  };

  const getStatusColor = (status: ScheduleEvent['status']) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority: ScheduleEvent['priority']) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-l-red-500';
      case 'high': return 'border-l-4 border-l-orange-500';
      case 'medium': return 'border-l-4 border-l-yellow-500';
      case 'low': return 'border-l-4 border-l-green-500';
      default: return 'border-l-4 border-l-gray-300';
    }
  };

  const filteredMembers = selectedMember === 'all' 
    ? teamMembers 
    : teamMembers.filter(member => member.id === selectedMember);

  const weekDates = getWeekDates(currentDate);
  const timeSlots = getTimeSlots();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">
            {scheduleEvents.filter(e => e.status === 'scheduled').length}
          </div>
          <div className="text-sm text-blue-800">Scheduled</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {scheduleEvents.filter(e => e.status === 'in_progress').length}
          </div>
          <div className="text-sm text-yellow-800">In Progress</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {scheduleEvents.filter(e => e.status === 'completed').length}
          </div>
          <div className="text-sm text-green-800">Completed</div>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-red-600">
            {scheduleEvents.filter(e => e.priority === 'urgent').length}
          </div>
          <div className="text-sm text-red-800">Urgent</div>
        </div>
      </div>

      {/* Header Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => viewMode === 'week' ? navigateWeek('prev') : navigateDay('prev')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            
            <h3 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
              {viewMode === 'week' 
                ? `Week of ${weekDates[0].toLocaleDateString('en-IE', { month: 'short', day: 'numeric' })} - ${weekDates[6].toLocaleDateString('en-IE', { month: 'short', day: 'numeric', year: 'numeric' })}`
                : currentDate.toLocaleDateString('en-IE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
              }
            </h3>
            
            <button
              onClick={() => viewMode === 'week' ? navigateWeek('next') : navigateDay('next')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <button
            onClick={() => setCurrentDate(new Date())}
            className="btn btn-outline btn-sm"
          >
            Today
          </button>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('day')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'day' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Day
            </button>
          </div>

          {/* Member Filter */}
          <select
            value={selectedMember}
            onChange={(e) => setSelectedMember(e.target.value)}
            className="form-select text-sm"
          >
            <option value="all">All Team Members</option>
            {teamMembers.map(member => (
              <option key={member.id} value={member.id}>
                {member.firstName} {member.lastName}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              // Set default time slot: first team member, current date, next available hour
              if (teamMembers.length > 0) {
                const defaultMember = selectedMember || teamMembers[0].id;
                const now = new Date();
                const defaultHour = now.getHours() + 1; // Next hour
                setSelectedTimeSlot({ 
                  memberId: defaultMember, 
                  date: currentDate, 
                  hour: defaultHour > 23 ? 9 : defaultHour // If past midnight, default to 9am
                });
                setShowAssignModal(true);
              }
            }}
            className="btn btn-primary btn-sm flex items-center"
            disabled={teamMembers.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Assign Job
          </button>
        </div>
      </div>

      {/* Schedule Grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Header Row */}
            <div className="grid grid-cols-8 bg-gray-50 border-b border-gray-200">
              <div className="p-3 text-sm font-medium text-gray-700 border-r border-gray-200">
                Time
              </div>
              {(viewMode === 'week' ? weekDates : [currentDate]).map((date, index) => (
                <div key={index} className="p-3 text-center border-r border-gray-200 last:border-r-0">
                  <div className="text-sm font-medium text-gray-900">
                    {date.toLocaleDateString('en-IE', { weekday: 'short' })}
                  </div>
                  <div className="text-xs text-gray-600">
                    {date.toLocaleDateString('en-IE', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Slots */}
            {timeSlots.map(hour => (
              <div key={hour} className="grid grid-cols-8 border-b border-gray-100 last:border-b-0">
                <div className="p-3 text-sm text-gray-600 border-r border-gray-200 bg-gray-50">
                  {hour.toString().padStart(2, '0')}:00
                </div>
                {(viewMode === 'week' ? weekDates : [currentDate]).map((date, dateIndex) => (
                  <div key={dateIndex} className="border-r border-gray-100 last:border-r-0 min-h-[80px]">
                    {filteredMembers.map(member => {
                      const events = getEventsForMemberAndTime(member.id, date, hour);
                      const isAvailable = getMemberAvailability(member, date, hour);
                      return (
                        <div key={member.id} className={`p-1 ${!isAvailable ? 'bg-gray-100 opacity-50' : ''}`}>
                          {events.length > 0 ? (
                            events.map(event => (
                              <div
                                key={event.id}
                                className={`p-2 rounded-md text-xs mb-1 cursor-pointer hover:shadow-sm transition-shadow ${getStatusColor(event.status)} ${getPriorityColor(event.priority)}`}
                                onClick={() => handleEventClick(event)}
                                title={`${event.jobTitle} - ${event.customerName}\n${event.jobLocation}\nStatus: ${event.status}\nPriority: ${event.priority}`}
                              >
                                <div className="font-medium truncate">{event.jobTitle}</div>
                                <div className="text-xs opacity-75 truncate">{event.customerName}</div>
                                <div className="flex items-center mt-1 text-xs opacity-75">
                                  <MapPin className="w-3 h-3 mr-1" />
                                  <span className="truncate">{event.jobLocation}</span>
                                </div>
                                {filteredMembers.length > 1 && (
                                  <div className="flex items-center mt-1 text-xs opacity-75">
                                    <User className="w-3 h-3 mr-1" />
                                    <span>{event.memberName}</span>
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <button
                              onClick={() => isAvailable ? handleTimeSlotClick(member.id, date, hour) : null}
                              className={`w-full h-full min-h-[60px] rounded-md transition-colors flex items-center justify-center ${
                                isAvailable 
                                  ? 'hover:bg-gray-50 text-gray-400 hover:text-gray-600 cursor-pointer' 
                                  : 'text-gray-300 cursor-not-allowed'
                              }`}
                              title={isAvailable ? "Click to assign a job" : "Member not available at this time"}
                              disabled={!isAvailable}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center">
          <div className="w-4 h-4 bg-blue-100 border border-blue-200 rounded mr-2"></div>
          <span>Scheduled</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-yellow-100 border border-yellow-200 rounded mr-2"></div>
          <span>In Progress</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
          <span>Completed</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
          <span>Cancelled</span>
        </div>
        <div className="ml-4 flex items-center">
          <div className="w-4 h-2 bg-red-500 rounded mr-2"></div>
          <span>Urgent</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-2 bg-orange-500 rounded mr-2"></div>
          <span>High Priority</span>
        </div>
      </div>

      {/* Job Assignment Modal */}
      {showAssignModal && (
        <JobAssignmentModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTimeSlot(null);
          }}
          onAssign={handleAssignJob}
          selectedTimeSlot={selectedTimeSlot}
          teamMembers={teamMembers}
          availableJobs={availableJobs}
          isJobsLoading={isJobsLoading}
          scheduleEvents={scheduleEvents}
        />
      )}
    </div>
  );
}

// Job Assignment Modal Component
interface JobAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (jobData: any) => void;
  selectedTimeSlot: { memberId: string; date: Date; hour: number } | null;
  teamMembers: TeamMember[];
  availableJobs: AvailableJob[];
  isJobsLoading: boolean;
  scheduleEvents: ScheduleEvent[];
}

function JobAssignmentModal({ isOpen, onClose, onAssign, selectedTimeSlot, teamMembers, availableJobs, isJobsLoading, scheduleEvents }: JobAssignmentModalProps) {
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState(selectedTimeSlot?.memberId || '');
  const [estimatedDuration, setEstimatedDuration] = useState(2);
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [notes, setNotes] = useState('');
  
  // Update selected member when time slot changes
  useEffect(() => {
    if (selectedTimeSlot?.memberId) {
      setSelectedMemberId(selectedTimeSlot.memberId);
    }
  }, [selectedTimeSlot]);
  
  const selectedMember = teamMembers.find(m => m.id === selectedMemberId);

  if (!isOpen || !selectedTimeSlot) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedJob || !selectedMemberId) return;

    const selectedJobData = availableJobs.find(job => job.id === selectedJob);
    onAssign({
      jobId: selectedJob,
      memberId: selectedMemberId,
      estimatedDuration,
      priority,
      notes,
      jobData: selectedJobData
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Assign Job to Team Member</h3>
        
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-600">Schedule for:</div>
          <div className="text-sm text-gray-600">
            {selectedTimeSlot.date.toLocaleDateString('en-IE', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at {selectedTimeSlot.hour.toString().padStart(2, '0')}:00
          </div>
        </div>

        {/* Conflict Warning */}
        {selectedTimeSlot && selectedMemberId && (
          <ConflictChecker 
            memberId={selectedMemberId}
            date={selectedTimeSlot.date}
            hour={selectedTimeSlot.hour}
            duration={estimatedDuration}
            scheduleEvents={scheduleEvents}
          />
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team Member Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Team Member *
            </label>
            <select
              value={selectedMemberId}
              onChange={(e) => setSelectedMemberId(e.target.value)}
              className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select a team member...</option>
              {teamMembers.filter(m => m.isActive).map(member => (
                <option key={member.id} value={member.id}>
                  {member.firstName} {member.lastName} - {member.role}
                </option>
              ))}
            </select>
            {selectedMember && (
              <div className="mt-2 text-xs text-gray-600">
                Rate: €{selectedMember.hourlyRate}/hr | Skills: {selectedMember.skills?.join(', ') || 'None listed'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Job *
            </label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="form-select w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">{isJobsLoading ? 'Loading jobs...' : 'Choose a job...'}</option>
              {availableJobs.map(job => (
                <option key={job.id} value={job.id}>
                  {job.title} - {job.customer} ({job.location}) - {job.priority.toUpperCase()}
                </option>
              ))}
            </select>
            {!isJobsLoading && availableJobs.length === 0 && (
              <p className="text-xs text-gray-500 mt-2">No available jobs to assign. Jobs must have status 'quote-accepted'.</p>
            )}
            {selectedJob && (
              <div className="mt-2 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600">
                  {availableJobs.find(job => job.id === selectedJob)?.description}
                </p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Duration (hours)
              </label>
              <input
                type="number"
                min="0.5"
                max="12"
                step="0.5"
                value={estimatedDuration}
                onChange={(e) => setEstimatedDuration(parseFloat(e.target.value))}
                className="form-input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as typeof priority)}
                className="form-select w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="form-textarea w-full"
              rows={3}
              placeholder="Add any special instructions or notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              Assign Job
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Conflict Checker Component
interface ConflictCheckerProps {
  memberId: string;
  date: Date;
  hour: number;
  duration: number;
  scheduleEvents: ScheduleEvent[];
}

function ConflictChecker({ memberId, date, hour, duration, scheduleEvents }: ConflictCheckerProps) {
  // Check for actual conflicts in the schedule
  const proposedStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0);
  const proposedEnd = new Date(proposedStart.getTime() + duration * 60 * 60 * 1000);
  
  const conflicts = scheduleEvents.filter(event => {
    if (event.memberId !== memberId) return false;
    
    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    
    // Check if times overlap
    return (
      (proposedStart >= eventStart && proposedStart < eventEnd) || // Starts during existing event
      (proposedEnd > eventStart && proposedEnd <= eventEnd) ||     // Ends during existing event
      (proposedStart <= eventStart && proposedEnd >= eventEnd)     // Completely overlaps existing event
    );
  });
  
  if (conflicts.length === 0) return null;

  return (
    <div className="p-3 bg-orange-50 border border-orange-200 rounded-md mb-4">
      <div className="flex items-start">
        <div className="w-5 h-5 text-orange-600 mr-2 mt-0.5">⚠️</div>
        <div>
          <h4 className="font-medium text-orange-900">Schedule Conflict Detected</h4>
          <p className="text-sm text-orange-800 mt-1">
            This team member already has {conflicts.length} job{conflicts.length > 1 ? 's' : ''} scheduled during this time. Please choose a different time or team member.
          </p>
          <ul className="text-sm text-orange-800 mt-2 space-y-1">
            {conflicts.map(conflict => {
              const start = new Date(conflict.startTime);
              const end = new Date(conflict.endTime);
              return (
                <li key={conflict.id}>
                  • {conflict.jobTitle} ({start.getHours().toString().padStart(2, '0')}:{start.getMinutes().toString().padStart(2, '0')} - {end.getHours().toString().padStart(2, '0')}:{end.getMinutes().toString().padStart(2, '0')})
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}